import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  UserRound, ShieldCheck, MonitorSmartphone, KeyRound, Umbrella,
  PhoneCall, Wrench, Rocket, ArrowDownRight, ArrowRight, Check, X, Wifi as WifiIcon,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { KineticHeading } from "@/components/motion/KineticHeading";
import { CTAButton } from "@/components/CTAButton";
import { PricingSelector } from "@/components/PricingSelector";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";
import { IMAGES } from "@/lib/api";
import { INCLUDED, GUARANTEES, PAY_ALREADY, FAQS, WIFI, NETWORK_NOTE } from "@/lib/content";

const SectionLabel = ({ children, dark }) => (
  <span className={`text-xs uppercase tracking-[0.25em] font-medium ${dark ? "text-amber-500" : "text-amber-600"}`}>{children}</span>
);

const MoreLink = ({ to, children, dark }) => (
  <Link to={to} className={`inline-flex items-center gap-2 font-medium link-underline ${dark ? "text-amber-400" : "text-navy"}`}>
    {children} <ArrowRight className="w-4 h-4" />
  </Link>
);

/* ---------------- HERO ---------------- */
const heroProofs = [
  "Un seul interlocuteur, une seule facture",
  "Intervention garantie en 4 heures",
  "Matériel neuf, renouvelé tous les 3 ans",
];

const Hero = () => {
  const { openAudit } = useAudit();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Bureau calme d'une petite entreprise" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-navy via-navy/90 to-navy/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/25 to-transparent" />
      </motion.div>

      {/* Lueurs pour la profondeur */}
      <div className="pointer-events-none absolute -bottom-40 -left-24 w-[46rem] h-[46rem] rounded-full bg-amber-500/20 blur-[130px]" />
      <div className="pointer-events-none absolute -top-10 right-0 w-[34rem] h-[34rem] rounded-full bg-[#0b264e]/50 blur-[120px]" />

      {/* Carte de preuve flottante (desktop) */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block absolute top-32 right-8 z-20 w-[21rem]"
      >
        <div className="rounded-2xl border border-white/12 bg-white/[0.05] backdrop-blur-md p-6 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.6)]">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.22em] font-semibold">Tout compris, sans surprise</p>
          <ul className="mt-5 space-y-4">
            {heroProofs.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-amber-500/15 grid place-items-center">
                  <Check className="w-3 h-3 text-amber-400" />
                </span>
                <span className="text-white/85 text-sm leading-snug">{p}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-white/55 text-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            Échange gratuit · sans engagement
          </div>
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pb-20 pt-36">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <SectionLabel dark>Maintenance et support informatique · Lille</SectionLabel>
        </motion.div>
        <h1 className="mt-6 font-display font-bold text-white tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.98] max-w-5xl">
          <KineticHeading lines={["Vous ne penserez", "plus jamais à votre", "informatique."]} highlightIndex={2} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
        Umbo accompagne les TPE de Lille avec une infogérance informatique tout compris.
        Un seul interlocuteur prend en charge votre maintenance, votre support, vos ordinateurs,
        votre connexion et vos sauvegardes — pour que vous puissiez vous concentrer sur votre activité.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }} className="mt-10 flex flex-col sm:flex-row gap-4">
          <CTAButton size="lg" withArrow onClick={openAudit} data-testid="hero-audit-cta">Parler de mon informatique</CTAButton>
          <CTAButton size="lg" variant="ghostLight" onClick={() => document.getElementById("tarifs")?.scrollIntoView({ behavior: "smooth" })}>Voir nos tarifs</CTAButton>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="mt-16 flex items-center gap-2 text-white/50 text-sm">
          <ArrowDownRight className="w-4 h-4" />
        <span>Infogérance informatique pour les TPE et petites entreprises de Lille et des Hauts-de-France</span>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- MARQUEE ---------------- */
const EditorialMarquee = () => (
  <section className="bg-white border-y border-slate-200 py-10 overflow-hidden" data-testid="marquee">
    <Marquee speed={40} gradient gradientColor="#ffffff" gradientWidth={120}>
      {["Un seul interlocuteur", "Une seule facture", "Prix affichés", "Clés de sécurité offertes", "Fibre + secours 4G", "Sans surprise"].map((t, i) => (
        <span key={i} className="mx-10 font-display text-3xl md:text-4xl font-medium text-navy/90 flex items-center gap-10">
          {t}<span className="w-2 h-2 rounded-full bg-amber-500" />
        </span>
      ))}
    </Marquee>
  </section>
);

/* ---------------- LE CONSTAT ---------------- */
const constats = [
  "Vous appelez un prestataire pour internet, un autre pour le téléphone, et un dépanneur quand un ordinateur ne démarre plus.",
  "Personne ne vérifie vraiment que votre sauvegarde fonctionne le jour où vous en aurez besoin.",
  "Vos ordinateurs vieillissent et ralentissent, et personne n'anticipe leur remplacement.",
  "La sécurité, on vous en parle le jour du problème — jamais avant.",
];

const Constat = () => (
  <section className="bg-white py-24 md:py-32" data-testid="section-constat">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <Reveal><SectionLabel>Le constat</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">
            Ne perdez plus votre temps à gérer votre informatique.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Dans une petite entreprise, chaque heure passée à résoudre un problème informatique est une heure
            que vous ne consacrez pas à votre activité. Pannes, prestataires multiples, sauvegardes,
            renouvellement des ordinateurs : ces sujets doivent être pris en charge, pas devenir votre quotidien.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Stagger className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
          {constats.map((c, i) => (
            <StaggerItem key={i} className="bg-white p-8">
              <span className="font-display text-sm text-amber-600 font-semibold">0{i + 1}</span>
              <p className="mt-3 text-navy text-lg leading-snug">{c}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);

/* ---------------- CHIFFRES CLÉS ---------------- */
const KEY_FIGURES = [
  { num: "4", unit: "h", label: "Intervention garantie, sur site ou à distance" },
  { num: "24/7", unit: "", label: "Surveillance permanente de vos systèmes" },
  { num: "2", unit: "", label: "Clés de sécurité physiques offertes, par personne" },
  { num: "3", unit: "ans", label: "Matériel neuf renouvelé — option renouvellement" },
];

const KeyFigures = () => (
  <section className="relative bg-navy overflow-hidden py-16 md:py-20" data-testid="section-figures">
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-navy-dark/50 via-navy to-navy" />
    <div className="pointer-events-none absolute -top-28 left-1/2 -translate-x-1/2 w-[52rem] h-[26rem] rounded-full bg-amber-500/10 blur-[120px]" />
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
      <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
        {KEY_FIGURES.map((f) => (
          <StaggerItem key={f.num + f.label} className="lg:px-8 lg:border-l lg:border-white/10 lg:first:border-l-0 lg:first:pl-0">
            <p className="font-display text-5xl md:text-6xl font-bold tracking-tighter text-white tabular-nums">
              {f.num}{f.unit && <span className="text-amber-500"> {f.unit}</span>}
            </p>
            <p className="mt-3 text-slate-300 text-sm leading-snug max-w-[15rem]">{f.label}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

/* ---------------- NOTRE RÉPONSE ---------------- */
const Reponse = () => {
  const { openAudit } = useAudit();
  return (
    <section className="bg-[#f8fafc] py-24 md:py-32" data-testid="section-reponse">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <img src={IMAGES.reponse} alt="Personne travaillant sereinement au bureau" className="w-full h-[26rem] object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-navy text-white rounded-xl p-6 shadow-xl max-w-[15rem]">
              <p className="font-display text-3xl font-bold">1 seul</p>
              <p className="text-slate-300 text-sm mt-1">numéro, contrat et facture pour toute votre informatique</p>
            </div>
          </div>
        </Reveal>
        <div>
          <Reveal><SectionLabel>Notre réponse</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">
              Votre informatique est prise en charge. Vous retrouvez du temps.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Avec notre infogérance à Lille, un seul interlocuteur prend en charge la maintenance,
              le support informatique, la connexion internet, la téléphonie, les postes de travail
              et les sauvegardes. Vous gagnez du temps et votre activité reste opérationnelle.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <CTAButton withArrow onClick={openAudit}>Faire le point gratuitement</CTAButton>
              <MoreLink to="/infogerance-informatique">Découvrir notre infogérance à Lille</MoreLink>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-slate-600">
              Notre informatique pour les{" "}
              <Link to="/informatique-cabinet-comptable-lille" className="text-navy font-medium link-underline">
                cabinets comptables
              </Link>{" "}
              et les{" "}
              <Link to="/informatique-notaire-lille" className="text-navy font-medium link-underline">
                études notariales
              </Link>{" "}
              à Lille.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- MANIFESTO (3 angles) ---------------- */
const chapters = [
  { n: "01", icon: UserRound, title: "Vous n’avez plus à gérer plusieurs prestataires",
    text: "Nous prenons en charge votre informatique, votre connexion internet, votre téléphonie, vos postes de travail, votre messagerie, vos sauvegardes et votre support. Vous gardez un seul interlocuteur qui connaît votre entreprise et coordonne l’ensemble pour vous.", to: "/infogerance-informatique" },
  { n: "02", icon: ShieldCheck, title: "Votre entreprise est protégée, quelle que soit sa taille",
    text: "Que vous soyez 2, 5 ou 20 collaborateurs, vous bénéficiez du même niveau de protection et de suivi. Vos postes, vos accès, vos données et vos sauvegardes sont protégés avec les mêmes standards, sans formule au rabais.", to: "/cybersecurite" },
  { n: "03", icon: MonitorSmartphone, title: "Votre matériel reste récent, sans grosse dépense imprévue",
    text: "Avec l’option de renouvellement, vos ordinateurs et équipements sont remplacés par du matériel neuf tous les 3 ans. Vous évitez les achats importants, les pannes liées au matériel vieillissant et les dépenses imprévues.", to: "/offres" },
];

const Manifesto = () => (
  <section className="bg-navy grain relative py-24 md:py-32 overflow-hidden" data-testid="section-manifesto">
    <div className="pointer-events-none absolute -top-20 right-[-10%] w-[42rem] h-[42rem] rounded-full bg-amber-500/10 blur-[150px]" aria-hidden />
    <div className="pointer-events-none absolute bottom-0 left-[-10%] w-[36rem] h-[36rem] rounded-full bg-[#0b264e]/60 blur-[130px]" aria-hidden />
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal><SectionLabel dark>Ce qui nous distingue</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white max-w-3xl text-balance">Une prise en charge globale, sans prestataires à coordonner.</h2>
      </Reveal>
      <div className="mt-16 space-y-px">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05}>
            <div className="group grid md:grid-cols-12 gap-6 md:gap-10 items-start py-10 border-t border-white/12">
              <div className="md:col-span-2"><span className="font-display text-6xl md:text-7xl font-bold text-white/10 group-hover:text-amber-500/80 transition-colors duration-500">{c.n}</span></div>
              <div className="md:col-span-1 pt-2"><c.icon className="w-8 h-8 text-amber-500" /></div>
              <div className="md:col-span-9 md:pl-6">
                <h3 className="font-display text-2xl md:text-3xl font-medium text-white tracking-tight">{c.title}</h3>
                <p className="mt-4 text-slate-300 text-lg leading-relaxed max-w-2xl">{c.text}</p>
                <div className="mt-5"><MoreLink to={c.to} dark>En savoir plus</MoreLink></div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CLÉS DE SÉCURITÉ ---------------- */
const SecurityKeys = () => (
  <section className="bg-white py-24 md:py-32" data-testid="section-keys">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div className="order-2 lg:order-1">
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5">
            <KeyRound className="w-4 h-4 text-amber-600" /><span className="text-amber-700 text-sm font-medium">Deux par personne, offertes</span>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">
            Protégez votre entreprise sans compliquer la vie de votre équipe.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Chaque personne dispose d'une protection simple pour accéder à ses comptes.
            Même si un mot de passe est volé, il ne suffit plus à ouvrir vos outils de travail.
            Nous installons et expliquons la solution pour que votre équipe puisse travailler sereinement.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <ul className="mt-8 space-y-4">
            {["Deux clés par personne : une principale et une de secours", "Le vol d'un mot de passe ne suffit plus à compromettre vos comptes", "Offertes : vous en devenez propriétaire et ne les restituez jamais"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-navy"><Check className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /><span>{t}</span></li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.2}><div className="mt-8"><MoreLink to="/cybersecurite">En savoir plus sur la sécurité</MoreLink></div></Reveal>
      </div>
      <Reveal className="order-1 lg:order-2">
        <div className="relative">
          <div className="absolute -inset-4 bg-amber-100/50 rounded-3xl -rotate-2" />
          <img src={IMAGES.keys} alt="Clé de sécurité physique posée près d'un ordinateur portable" className="relative w-full h-[28rem] object-cover rounded-2xl shadow-xl" loading="lazy" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- WIFI VISITEURS ---------------- */
const WifiVisiteurs = () => (
  <section className="bg-navy-light grain relative py-24 md:py-28 overflow-hidden" data-testid="section-wifi">
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5">
            <WifiIcon className="w-4 h-4 text-amber-500" /><span className="text-amber-400 text-sm font-medium">{WIFI.eyebrow}</span>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">{WIFI.title}</h2>
        </Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-lg text-slate-300 leading-relaxed">{WIFI.context}</p></Reveal>
        <Reveal delay={0.15}><p className="mt-4 text-slate-400">{WIFI.audience}</p></Reveal>
        <Reveal delay={0.2}><div className="mt-8"><MoreLink to="/cybersecurite" dark>En savoir plus</MoreLink></div></Reveal>
      </div>
      <div className="lg:col-span-7">
        <Stagger className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden">
          {WIFI.items.map((it, i) => (
            <StaggerItem key={it} className={`bg-navy p-7 ${i === WIFI.items.length - 1 && WIFI.items.length % 2 ? "sm:col-span-2" : ""}`}>
              <Check className="w-5 h-5 text-amber-500" />
              <p className="mt-3 text-white/90 leading-snug">{it}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);

/* ---------------- ASSURABILITÉ ---------------- */
const Insurance = () => (
  <section className="bg-[#f8fafc] py-20 md:py-24 border-y border-slate-200" data-testid="section-insurance">
    <div className="mx-auto max-w-5xl px-5 sm:px-8 flex flex-col md:flex-row gap-8 items-start">
      <div className="shrink-0"><div className="w-14 h-14 rounded-xl bg-navy grid place-items-center"><Umbrella className="w-7 h-7 text-amber-500" /></div></div>
      <div>
        <Reveal><SectionLabel>Assurabilité</SectionLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">Vous serez enfin assurable.</h2></Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Les assureurs qui couvrent le risque informatique exigent aujourd'hui un socle de mesures
            précis pour assurer une petite entreprise. Beaucoup de dirigeants découvrent, au moment du
            sinistre, qu'ils ne le remplissaient pas. Notre offre coche ces critères par construction,
            ce qui facilite l'obtention d'une couverture auprès de votre propre assureur.
          </p>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Pour être clair : nous ne vendons ni ne fournissons aucune assurance. Nous mettons
            simplement votre entreprise en conformité avec ce que les assureurs attendent.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- CE QUE VOUS PAYEZ DÉJÀ ---------------- */
const PayAlready = () => (
  <section className="bg-white py-24 md:py-32" data-testid="section-pay-already">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl mb-14">
        <Reveal><SectionLabel>Avant de parler de prix</SectionLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Ce que vous payez déjà, en désordre.</h2></Reveal>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Reveal>
          <div className="h-full border border-slate-200 rounded-2xl p-8">
            <h3 className="font-display text-xl font-medium text-navy">{PAY_ALREADY.left.title}</h3>
            <ul className="mt-6 space-y-3">
              {PAY_ALREADY.left.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-slate-600"><div className="w-5 h-5 rounded-full bg-slate-100 grid place-items-center mt-0.5 shrink-0"><X className="w-3 h-3 text-slate-400" /></div><span>{it}</span></li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-slate-500 italic">{PAY_ALREADY.left.footer}</p>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="h-full border-2 border-amber-200 bg-amber-50/40 rounded-2xl p-8">
            <h3 className="font-display text-xl font-medium text-navy">{PAY_ALREADY.right.title}</h3>
            <ul className="mt-6 space-y-3">
              {PAY_ALREADY.right.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-navy"><Check className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /><span>{it}</span></li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-amber-800 italic">{PAY_ALREADY.right.footer}</p>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.12}>
        <p className="mt-12 mx-auto max-w-3xl text-center font-display text-2xl sm:text-3xl font-medium text-navy tracking-tight text-balance">
          {PAY_ALREADY.bridge}
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---------------- OFFRES + INCLUS + PRIX ---------------- */
const Pricing = () => (
  <section className="bg-navy-dark grain relative py-24 md:py-32 overflow-hidden">
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <Reveal><SectionLabel dark>Notre offre</SectionLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">Une infogérance informatique claire, avec un tarif adapté à votre équipe.</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-5 text-lg text-slate-300">Maintenance, support informatique et continuité de service : tout est pris en charge dans une seule offre, avec un forfait tout compris selon la taille de votre équipe.</p></Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4 max-w-5xl mx-auto mb-8">
          {INCLUDED.map((it) => (
            <div key={it} className="flex items-start gap-3 text-slate-200"><Check className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" /><span className="text-sm leading-snug">{it}</span></div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="max-w-5xl mx-auto mb-8 text-slate-400 text-sm leading-relaxed">{NETWORK_NOTE}</p>
      </Reveal>
      <Reveal delay={0.14}>
        <div className="text-center mb-20"><MoreLink to="/ce-que-vous-obtenez" dark>Voir en détail, concrètement, ce que vous obtenez</MoreLink></div>
      </Reveal>

      <div className="text-center max-w-2xl mx-auto mb-12">
        <Reveal><h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">Un tarif simple, adapté à votre équipe.</h3></Reveal>
        <Reveal delay={0.05}><p className="mt-4 text-slate-300">Choisissez votre forfait selon le nombre de personnes : tout est compris, avec un budget prévisible.</p></Reveal>
      </div>
      <Reveal delay={0.1}><PricingSelector /></Reveal>
    </div>
  </section>
);

/* ---------------- GARANTIES ---------------- */
const Guarantees = () => (
  <section className="bg-white py-24 md:py-32" data-testid="section-guarantees">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal><SectionLabel>Ce que nous garantissons</SectionLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Des engagements clairs, écrits noir sur blanc.</h2></Reveal>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden shadow-[0_30px_60px_-40px_rgba(7,27,58,0.35)]">
        {GUARANTEES.map((g, i) => (
          <Reveal key={g.title} delay={i * 0.05} className="group relative bg-white p-8 hover:bg-[#f8fafc] transition-colors">
            <span className="absolute top-0 left-0 h-0.5 w-0 bg-amber-500 group-hover:w-16 transition-[width] duration-500" aria-hidden />
            <span className="font-display text-3xl font-bold text-slate-200 group-hover:text-amber-500 transition-colors duration-300">0{i + 1}</span>
            <h3 className="mt-4 font-display text-xl font-medium text-navy">{g.title}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{g.text}</p>
          </Reveal>
        ))}
        <div className="hidden lg:flex bg-navy p-8 flex-col justify-center" aria-hidden>
          <Check className="w-6 h-6 text-amber-500" />
          <p className="mt-3 font-display text-lg text-white leading-snug">Et tout figure noir sur blanc dans votre contrat.</p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- COMMENT ÇA SE PASSE ---------------- */
const steps = [
  { icon: PhoneCall, title: "Le premier échange", text: "Un échange gratuit de 30 minutes pour comprendre votre situation, sans engagement." },
  { icon: Wrench, title: "La mise en place", text: "Nous préparons tout dans le détail et convenons ensemble d'un calendrier de mise en service adapté à votre activité." },
  { icon: Rocket, title: "La tranquillité", text: "Votre informatique tourne. Nous surveillons, entretenons et intervenons selon nos engagements." },
];

const HowItWorks = () => (
  <section className="bg-[#f8fafc] py-24 md:py-32" data-testid="section-how">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal><SectionLabel>Comment ça se passe</SectionLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Du premier échange à la tranquillité.</h2></Reveal>
      </div>
      <div className="mt-20 relative">
        {/* Ligne de liaison (desktop) */}
        <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" aria-hidden />
        <div className="grid md:grid-cols-3 gap-y-12 md:gap-x-10">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <div className="relative flex flex-col">
                <div className="flex items-center gap-5 md:gap-0 md:block">
                  <div className="relative z-10 w-16 h-16 rounded-2xl bg-navy grid place-items-center shadow-[0_16px_40px_-16px_rgba(7,27,58,0.6)] ring-8 ring-[#f8fafc]">
                    <s.icon className="w-7 h-7 text-amber-500" />
                  </div>
                  <span className="md:hidden font-display text-4xl font-bold text-slate-200 tabular-nums">0{i + 1}</span>
                </div>
                <span className="hidden md:inline-block mt-7 font-display text-xs font-semibold uppercase tracking-[0.2em] text-amber-600">Étape 0{i + 1}</span>
                <h3 className="mt-2 md:mt-3 font-display text-xl font-medium text-navy">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-sm">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
const FAQ = () => (
  <section className="bg-white py-24 md:py-32" data-testid="section-faq">
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <div className="text-center mb-14">
        <Reveal><SectionLabel>Questions fréquentes</SectionLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy">Les réponses aux questions des dirigeants de TPE.</h2></Reveal>
      </div>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-amber-600 hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

/* ---------------- CTA FINAL ---------------- */
const FinalCTA = () => {
  const { openAudit } = useAudit();
  return (
    <section className="bg-navy grain relative py-28 md:py-36 overflow-hidden" data-testid="section-final-cta">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-navy-dark/50 via-navy to-navy-dark/50" aria-hidden />
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[54rem] h-[30rem] rounded-full bg-amber-500/15 blur-[140px]" aria-hidden />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 text-center">
        <Reveal><h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tighter text-white text-balance">Retrouvez du temps pour votre activité.</h2></Reveal>
        <Reveal delay={0.08}><p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">30 minutes suffisent pour comprendre votre situation et voir comment simplifier votre informatique. Un échange clair, sans jargon et sans engagement.</p></Reveal>
        <Reveal delay={0.16}><CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="final-audit-cta">Faire le point gratuitement</CTAButton></Reveal>
        <Reveal delay={0.24}><p className="mt-6 text-slate-400 text-sm">Ou appelez-nous directement au 03 20 49 02 04</p></Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
<Seo
  title="Infogérance informatique à Lille pour TPE"
  description="Umbo accompagne les TPE de Lille avec une infogérance informatique tout compris : maintenance, support, dépannage, sauvegardes et continuité de service. Un seul interlocuteur, une seule facture."
  path="/"
/>      
      <Hero />
      <EditorialMarquee />
      <Constat />
      <KeyFigures />
      <Reponse />
      <Manifesto />
      <SecurityKeys />
      <WifiVisiteurs />
      <Insurance />
      <PayAlready />
      <Pricing />
      <Guarantees />
      <HowItWorks />
      <FAQ />
      <FinalCTA />
    </>
  );
}
